import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { AppPicker } from './AppPicker.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface FormRow {
  readonly formId: string;
  readonly formCode: string;
  readonly formName: string;
  readonly formType: string;
  readonly status: string;
  readonly versionNo: number;
}
interface FieldRow {
  readonly formFieldId: string;
  readonly fieldCode: string;
  readonly fieldLabel: string;
  readonly fieldType: string;
  readonly isMandatory: boolean;
  readonly sortOrder: number;
}

const FORM_TYPES = ['STANDARD', 'WIZARD', 'SURVEY', 'CHECKLIST', 'APPROVAL'];
const FIELD_TYPES = ['TEXT', 'NUMBER', 'DATE', 'DROPDOWN', 'MULTI_SELECT', 'CHECKBOX', 'RADIO', 'FILE_UPLOAD', 'IMAGE_UPLOAD', 'SIGNATURE', 'RICH_TEXT', 'CALCULATED'];
const OPTION_TYPES = ['DROPDOWN', 'MULTI_SELECT', 'RADIO'];

/**
 * M40 — Form Designer (§7/§8). The forms authoring surface over T524 / T525.
 * Create a form under an application, add fields of any of the twelve control
 * types (option fields carry their choices), then publish it. Viewing needs
 * FORM_VIEW; authoring needs FORM_MANAGE; publish needs FORM_PUBLISH (SoD).
 */
export function FormDesigner(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [applicationId, setApplicationId] = useState('');
  const [rows, setRows] = useState<FormRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState('STANDARD');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [selected, setSelected] = useState<FormRow | null>(null);
  const [fields, setFields] = useState<FieldRow[]>([]);
  const [fieldsError, setFieldsError] = useState<string | null>(null);
  const [fieldLabel, setFieldLabel] = useState('');
  const [fieldType, setFieldType] = useState('TEXT');
  const [isMandatory, setIsMandatory] = useState(false);
  const [optionsCsv, setOptionsCsv] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (applicationId) params.set('applicationId', applicationId);
    params.set('pageSize', '100');
    apiRequest<{ data: FormRow[]; total: number }>(`/v1/app-platform/forms?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, applicationId, reload]);

  useEffect(() => {
    if (!selected) { setFields([]); setFieldsError(null); return; }
    let alive = true;
    apiRequest<{ data: FieldRow[] }>(`/v1/app-platform/forms/${selected.formId}/fields`, { token })
      .then((r) => { if (alive) { setFields(r.data); setFieldsError(null); } })
      // Unread fields are not "no fields".
      .catch((err) => { if (alive) { setFields([]); setFieldsError((err as Error).message); } });
    return () => { alive = false; };
  }, [token, selected, reload]);

  const createForm = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!applicationId.trim()) { setActionError(t('m40.common.appIdRequired')); return; }
    if (!formName.trim()) { setActionError(t('m40.formDesigner.nameRequired')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: FormRow }>(`/v1/app-platform/forms`, {
        method: 'POST',
        body: { applicationId: applicationId.trim(), formName: formName.trim(), formType },
        token,
      });
      setNotice(t('m40.common.created', { code: r.data.formCode }));
      setFormName('');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m40.formDesigner.notAuthManage'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const addField = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!selected) return;
    if (!fieldLabel.trim()) { setActionError(t('m40.formDesigner.fieldLabelRequired')); return; }
    const body: Record<string, unknown> = { fieldLabel: fieldLabel.trim(), fieldType, isMandatory };
    if (OPTION_TYPES.includes(fieldType)) {
      const opts = optionsCsv.split(',').map((o) => o.trim()).filter(Boolean).map((value) => ({ value }));
      body.options = opts;
    }
    try {
      const r = await apiRequest<{ data: FieldRow }>(`/v1/app-platform/forms/${selected.formId}/fields`, {
        method: 'POST',
        body,
        token,
      });
      setNotice(t('m40.formDesigner.fieldAdded', { code: r.data.fieldCode }));
      setFieldLabel('');
      setOptionsCsv('');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m40.formDesigner.notAuthManage'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const publish = async (row: FormRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/app-platform/forms/${row.formId}/publish`, { method: 'POST', token });
      setNotice(t('m40.common.published', { code: row.formCode }));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m40.formDesigner.notAuthPublish'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="form-designer">
      <h1>{t('m40.formDesigner.title')}</h1>

      <section aria-label="form-scope">
        <AppPicker
          label={t('m40.common.applicationId')}
          value={applicationId}
          onPick={(id) => { setApplicationId(id); setSelected(null); }}
        />
      </section>

      <section aria-label="create-form">
        <h2>{t('m40.formDesigner.newForm')}</h2>
        <form aria-label="form-form" onSubmit={(e) => void createForm(e)}>
          <label>{t('m40.common.name')}<input aria-label="field-form-name" value={formName} onChange={(e) => setFormName(e.target.value)} /></label>
          <label>{t('m40.common.type')}
            <select aria-label="field-form-type" value={formType} onChange={(e) => setFormType(e.target.value)}>
              {FORM_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-form" disabled={busy}>{busy ? t('m40.common.creating') : t('m40.formDesigner.createBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="form-error">{actionError}</p>}
        {notice && <p aria-label="form-notice">{notice}</p>}
      </section>

      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m40.common.loading')}</p> : (
        <section aria-label="form-list">
          <p aria-label="form-total">{t('m40.formDesigner.total', { count: rows.length })}</p>
          <DataTable<FormRow>
            ariaLabel="form-table"
            testId="form-table"
            rowKey={(r) => r.formId}
            rowDataAttrs={(r) => ({ 'data-form': r.formCode })}
            emptyMessage={t('m40.formDesigner.empty')}
            rows={rows}
            columns={[
              { key: 'code', header: t('m40.common.code'), render: (r) => r.formCode },
              { key: 'name', header: t('m40.common.name'), render: (r) => r.formName },
              { key: 'type', header: t('m40.common.type'), render: (r) => r.formType },
              { key: 'status', header: t('m40.common.status'), cellAriaLabel: (r) => `status-${r.formCode}`, render: (r) => r.status },
              { key: 'version', header: t('m40.common.version'), render: (r) => `v${r.versionNo}` },
              { key: 'actions', header: t('m40.common.actions'), render: (r) => (
                <>
                  <Button variant="secondary" type="button" aria-label={`design-${r.formCode}`} onClick={() => setSelected(r)}>{t('m40.formDesigner.design')}</Button>
                  <Button variant="primary" type="button" aria-label={`publish-${r.formCode}`} onClick={() => void publish(r)}>{t('m40.common.publish')}</Button>
                </>
              ) },
            ]}
          />
        </section>
      )}

      {selected && (
        <section aria-label="field-designer">
          <h2>{t('m40.formDesigner.fieldsHeading', { code: selected.formCode })}</h2>
          <form aria-label="field-form" onSubmit={(e) => void addField(e)}>
            <label>{t('m40.formDesigner.fieldLabel')}<input aria-label="field-label" value={fieldLabel} onChange={(e) => setFieldLabel(e.target.value)} /></label>
            <label>{t('m40.common.type')}
              <select aria-label="field-type" value={fieldType} onChange={(e) => setFieldType(e.target.value)}>
                {FIELD_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
              </select>
            </label>
            <label>{t('m40.formDesigner.mandatory')}<input type="checkbox" aria-label="field-mandatory" checked={isMandatory} onChange={(e) => setIsMandatory(e.target.checked)} /></label>
            {OPTION_TYPES.includes(fieldType) && (
              <label>{t('m40.formDesigner.optionsLabel')}<input aria-label="field-options" value={optionsCsv} onChange={(e) => setOptionsCsv(e.target.value)} /></label>
            )}
            <Button variant="primary" type="submit" aria-label="submit-field">{t('m40.formDesigner.addField')}</Button>
          </form>
          {fieldsError ? <p role="alert" aria-label="fields-error">{fieldsError}</p> : (
          <DataTable<FieldRow>
            ariaLabel="field-table"
            testId="field-table"
            rowKey={(f) => f.formFieldId}
            rowDataAttrs={(f) => ({ 'data-field': f.fieldCode })}
            emptyMessage={t('m40.formDesigner.noFields')}
            rows={fields}
            columns={[
              { key: 'code', header: t('m40.common.code'), render: (f) => f.fieldCode },
              { key: 'label', header: t('m40.formDesigner.fieldLabel'), render: (f) => f.fieldLabel },
              { key: 'type', header: t('m40.common.type'), cellAriaLabel: (f) => `field-type-${f.fieldCode}`, render: (f) => f.fieldType },
              { key: 'mandatory', header: t('m40.formDesigner.mandatory'), cellAriaLabel: (f) => `field-mandatory-${f.fieldCode}`, render: (f) => f.isMandatory ? t('m40.common.yes') : t('m40.common.no') },
            ]}
          />
          )}
        </section>
      )}
    </main>
  );
}
